import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import ChannelIcon from './ChannelIcon';
import { generateCampaignProposalPdf } from '../../utils/campaignProposalPdf';
import { graphqlApi } from '../../services/graphql';
import {
  formatDate,
  getDaysRemaining,
  getProgressPercent,
  getChannels,
  getGradient,
  getCategoryLabel,
} from '../../utils/campaignUtils';
import { USER_TYPES } from '../../constants/userTypes';
import type { CampaignApplicationStats } from '../../hooks/useCampaignApplicationStats';

interface CampaignGridCardProps {
  campaign: any;
  index: number;
  linkPrefix?: string; // default: '/campaigns'
  userType?: string;   // brand, agency, influencer — controls action buttons
  applicationStats?: CampaignApplicationStats; // optional per-campaign counters (brand/agency only)
  onStatusChange?: (campaignId: string, newStatus: string) => void; // parent sync for active↔paused toggle
}

const CampaignGridCard: React.FC<CampaignGridCardProps> = ({ campaign: c, index, linkPrefix = '/campaigns', userType, applicationStats, onStatusChange }) => {
  const { t } = useTranslation('common');
  const { t: tc } = useTranslation('campaigns');
  const img = c.representativeImages?.[0]?.fileUrl;
  const channels = getChannels(c);
  const days = getDaysRemaining(c.recruitmentPeriod?.endDate);
  const progress = getProgressPercent(c.recruitmentPeriod?.startDate, c.recruitmentPeriod?.endDate);
  const gradient = getGradient(index);
  const isExpired = c.recruitmentPeriod?.endDate && !c.recruitmentPeriod?.alwaysRecruiting
    ? new Date(c.recruitmentPeriod.endDate) < new Date()
    : false;
  const showExpiredStyle = isExpired && !userType; // Only gray on public dashboard (no userType)

  // Campaign-owner status badge: only rendered for host-like viewers (brand / agency)
  // so influencers browsing campaigns don't see the owner's internal workflow state.
  // Active / paused / draft / completed / cancelled come straight from Campaign.status.
  const isOwnerView = userType === USER_TYPES.BRAND || userType === USER_TYPES.AGENCY;
  const status: string | undefined = c.status;
  // `active` and `paused` are presented as a toggle switch on owner views; the
  // other statuses (draft / completed / cancelled) stay as static badges because
  // they can't be toggled from the grid.
  const isToggleable = isOwnerView && (status === 'active' || status === 'paused');
  const statusBadge = isOwnerView && status && !isToggleable
    ? (() => {
        const cls: Record<string, string> = {
          active: 'bg-green-500 text-white',
          draft: 'bg-gray-500 text-white',
          paused: 'bg-yellow-500 text-white',
          completed: 'bg-blue-500 text-white',
          cancelled: 'bg-red-500 text-white',
        };
        const i18nKey: Record<string, string> = {
          active: 'publicDashboard.statusActive',
          draft: 'publicDashboard.statusDraft',
          paused: 'publicDashboard.statusPaused',
          completed: 'publicDashboard.statusCompleted',
          cancelled: 'publicDashboard.statusCancelled',
        };
        return {
          className: cls[status] ?? 'bg-gray-500 text-white',
          label: t(i18nKey[status] ?? 'publicDashboard.status', {
            defaultValue: status.charAt(0).toUpperCase() + status.slice(1),
          }),
        };
      })()
    : null;

  // Toggle switch state + handler (active ↔ paused). Local `updating` flag
  // prevents double-clicks; local `localStatus` lets the switch reflect the
  // new state immediately while the mutation is in flight.
  const [localStatus, setLocalStatus] = React.useState<string | undefined>(c.status);
  const [updating, setUpdating] = React.useState(false);
  React.useEffect(() => { setLocalStatus(c.status); }, [c.status]);
  const isActive = localStatus === 'active';
  // Reuse the pause guard from CampaignDetailPage: block pausing while the
  // campaign still has live applicants so influencers don't get stranded.
  const pauseBlocked = isActive && (applicationStats?.applied ?? 0) > 0;

  const handleToggleStatus = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!c?.campaignId || updating) return;
    const newStatus = isActive ? 'paused' : 'active';
    if (newStatus === 'paused' && pauseBlocked) {
      toast.error(
        tc('messages.cannotPauseWithApplicants', {
          defaultValue: '신청자가 있는 캠페인은 비활성화할 수 없습니다',
        }),
      );
      return;
    }
    setUpdating(true);
    const prev = localStatus;
    setLocalStatus(newStatus); // optimistic
    try {
      // Cast to `any` because `updateCampaign`'s typed input is the form DTO
      // and doesn't include `status`; the backend accepts the field though,
      // same pattern as CampaignDetailPage.
      const response = await graphqlApi.updateCampaign(c.campaignId, { status: newStatus } as any);
      if (response.success) {
        toast.success(tc('messages.statusUpdated'));
        onStatusChange?.(c.campaignId, newStatus);
      } else {
        setLocalStatus(prev);
        toast.error(response.message || tc('messages.statusUpdateFailed'));
      }
    } catch (err: any) {
      console.error('Error updating campaign status from card:', err);
      setLocalStatus(prev);
      toast.error(err?.message || tc('messages.statusUpdateFailed'));
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className={`bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg hover:border-gray-300 transition-all duration-200 flex flex-col ${showExpiredStyle ? 'opacity-60 grayscale' : ''}`}>
      {/* Thumbnail */}
      <div className="relative h-40 overflow-hidden">
        {img ? (
          <img src={img} alt={c.campaignName || ''} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className={`w-full h-full bg-gradient-to-br ${gradient} flex items-center justify-center`}>
            <span className="text-white/80 text-3xl font-bold">{(c.campaignName || 'C')[0]}</span>
          </div>
        )}
        {c.campaignCategory && (
          <span className="absolute top-2 left-2 px-2 py-0.5 bg-white/90 backdrop-blur-sm text-[11px] font-medium text-gray-700 rounded-full">
            {t(`categories.${c.campaignCategory}`, { defaultValue: getCategoryLabel(c.campaignCategory) })}
          </span>
        )}
        {statusBadge && (
          <span className={`absolute top-2 right-2 px-2 py-0.5 text-[11px] font-bold rounded-full ${statusBadge.className}`}>
            {statusBadge.label}
          </span>
        )}
        {isToggleable && (
          <div className="absolute top-2 right-2 flex items-center gap-1.5 px-2 py-0.5 bg-white/70 backdrop-blur-sm rounded-full shadow-sm">
            <span className={`text-[11px] font-bold ${isActive ? 'text-green-600' : 'text-gray-500'}`}>
              {isActive ? t('publicDashboard.statusActive') : t('publicDashboard.statusPaused')}
            </span>
            <button
              type="button"
              onClick={handleToggleStatus}
              disabled={updating || pauseBlocked}
              role="switch"
              aria-checked={isActive}
              aria-label={isActive ? tc('actions.pauseTooltip') : tc('actions.activateTooltip')}
              title={
                pauseBlocked
                  ? tc('messages.cannotPauseWithApplicants', {
                      defaultValue: '신청자가 있는 캠페인은 비활성화할 수 없습니다',
                    })
                  : isActive
                    ? tc('actions.pauseTooltip')
                    : tc('actions.activateTooltip')
              }
              className={`relative inline-flex h-4 w-8 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed ${
                isActive ? 'bg-green-500 focus:ring-green-500' : 'bg-gray-300 focus:ring-gray-400'
              }`}
            >
              <span
                className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform ${
                  isActive ? 'translate-x-4' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>
        )}
        {showExpiredStyle ? (
          <span className={`absolute right-2 px-2 py-0.5 text-[11px] font-bold rounded-full bg-gray-500 text-white ${(statusBadge || isToggleable) ? 'top-9' : 'top-2'}`}>
            {t('publicDashboard.closed')}
          </span>
        ) : !isOwnerView && days !== null && days >= 0 && (
          // D-day pill is for public/influencer viewers only — owners see the
          // D-day count inline with the recruitment period in the body instead.
          <span className={`absolute right-2 px-2 py-0.5 text-[11px] font-bold rounded-full ${
            days < 3 ? 'bg-red-500 text-white' : 'bg-gray-900/70 text-white'
          } ${(statusBadge || isToggleable) ? 'top-9' : 'top-2'}`}>
            D-{days}
          </span>
        )}
      </div>

      {/* Body */}
      <div className="p-3.5 flex flex-col flex-1">
        {c.brandName && (
          <p className="text-xs text-gray-400 font-medium mb-1 truncate">{c.brandName}</p>
        )}
        <h3 className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2 mb-2">
          {c.campaignName || t('publicDashboard.campaign')}
        </h3>

        {channels.length > 0 && (
          <div className="flex items-center gap-1.5 mb-2">
            {channels.map((ch: string, i: number) => (
              <ChannelIcon key={i} channel={ch} className="w-4 h-4" />
            ))}
          </div>
        )}

        <div className="mb-2">
          <span className="inline-block px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-semibold rounded-md">
            ${(c.rewardAmount != null ? c.rewardAmount : 0).toLocaleString()} {t('publicDashboard.reward')}
          </span>
        </div>

        <div className="flex-1" />

        {c.recruitmentPeriod && (
          <div className="mb-3">
            <div className="flex items-center justify-between text-[11px] text-gray-400 mb-1">
              <span>{formatDate(c.recruitmentPeriod.startDate)} ~ {formatDate(c.recruitmentPeriod.endDate)}</span>
              {isOwnerView ? (
                days !== null && days >= 0 ? (
                  <span className="font-bold text-blue-600">
                    {t('publicDashboard.daysRemaining', { count: days })}
                  </span>
                ) : null
              ) : c.numberOfRecruits != null ? (
                <span>{t('publicDashboard.recruits', { count: c.numberOfRecruits })}</span>
              ) : null}
            </div>
            <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 rounded-full transition-all"
                style={{ width: `${Math.min(progress, 100)}%` }}
              />
            </div>
            {isOwnerView && (() => {
              // Always show the counter strip for owner views; default to
              // zeros when the campaign has no applications yet so brand users
              // still see the `모집 N명 | 신청 0명 | 승인대기 0명 | 진행확정 0/N명`
              // skeleton instead of an empty gap.
              const s = applicationStats ?? { applied: 0, pendingApproval: 0, confirmed: 0 };
              const total = c.numberOfRecruits ?? 0;
              return (
                <div className="mt-2 flex items-center justify-between gap-1 text-[11px] text-gray-600 whitespace-nowrap overflow-hidden">
                  <span>
                    {t('publicDashboard.slotsRecruit')}{' '}
                    <strong className="text-gray-900">{total}</strong>{t('publicDashboard.personSuffix')}
                  </span>
                  <span className="text-gray-300">|</span>
                  <span>
                    {t('publicDashboard.slotsApplied')}{' '}
                    <strong className="text-gray-900">{s.applied}</strong>{t('publicDashboard.personSuffix')}
                  </span>
                  <span className="text-gray-300">|</span>
                  <span>
                    {t('publicDashboard.slotsPending')}{' '}
                    <strong className={s.pendingApproval > 0 ? 'text-amber-600' : 'text-gray-900'}>
                      {s.pendingApproval}
                    </strong>
                    {t('publicDashboard.personSuffix')}
                  </span>
                  <span className="text-gray-300">|</span>
                  <span>
                    {t('publicDashboard.slotsConfirmed')}{' '}
                    <strong className={s.confirmed > 0 ? 'text-green-600' : 'text-gray-900'}>
                      {s.confirmed}
                    </strong>
                    /{total}
                    {t('publicDashboard.personSuffix')}
                  </span>
                </div>
              );
            })()}
          </div>
        )}

        {/* Action buttons */}
        {userType === USER_TYPES.BRAND ? (
          <div className="flex flex-col gap-1.5">
            <Link to={`/campaigns/${c.campaignId}/detail`} className="w-full text-center py-2 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
{t('publicDashboard.campaignDetail_btn')}
            </Link>
            <Link to={`/campaigns/${c.campaignId}/applicants`} className="w-full text-center py-2 text-xs font-medium text-purple-600 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors">
{t('publicDashboard.campaignManage')}
            </Link>
            <span className="w-full text-center py-2 text-xs font-medium text-gray-400 bg-gray-50 rounded-lg cursor-not-allowed">
{t('publicDashboard.resultReport')}
            </span>
          </div>
        ) : userType === USER_TYPES.AGENCY ? (
          <div className="flex flex-col gap-1.5">
            <Link to={`/campaigns/${c.campaignId}/detail`} className="w-full text-center py-2 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
{t('publicDashboard.campaignDetail_btn')}
            </Link>
            <button onClick={() => generateCampaignProposalPdf(c, t)} className="w-full text-center py-2 text-xs font-medium text-amber-600 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors">
{t('publicDashboard.proposal')}
            </button>
            <Link to={`/campaigns/${c.campaignId}/applicants`} className="w-full text-center py-2 text-xs font-medium text-purple-600 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors">
{t('publicDashboard.campaignManage')}
            </Link>
            <span className="w-full text-center py-2 text-xs font-medium text-gray-400 bg-gray-50 rounded-lg cursor-not-allowed">
{t('publicDashboard.resultReport')}
            </span>
          </div>
        ) : isExpired ? (
          <span className="block w-full text-center py-2 text-sm font-medium text-gray-400 bg-gray-100 rounded-lg cursor-not-allowed">
            {t('publicDashboard.recruitmentClosed', '모집마감')}
          </span>
        ) : (
          <Link
            to={`${linkPrefix}/${c.campaignId}`}
            className="block w-full text-center py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
          >
            {t('publicDashboard.viewDetail')}
          </Link>
        )}
      </div>
    </div>
  );
};

export default CampaignGridCard;
